import { useEffect, useRef, type FC, type ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import useOnlineStatus, { type OnlineStatusConfig } from '../hooks/useOnlineStatus';
import { OnlineStatusContext } from './OnlineStatusContext';

interface OnlineStatusProviderProps {
  children: ReactNode;
  // Konfiguracja dla hooka
  offlineDelay?: number;
  onlineDelay?: number;
  verifyConnection?: boolean;
  // Czy przekierowywać na stronę offline po długiej przerwie
  redirectToOfflineMode?: boolean;
}

// Provider component that will distribute the online status
export const OnlineStatusProvider: FC<OnlineStatusProviderProps> = ({ 
  children,
  offlineDelay = 3000,
  onlineDelay = 1000,
  verifyConnection = true,
  redirectToOfflineMode = true,
}) => {
  const config: OnlineStatusConfig = { offlineDelay, onlineDelay, verifyConnection };
  const onlineStatus = useOnlineStatus(config);
  const router = useRouter();
  const previousOnlineRef = useRef(onlineStatus.isOnline);
  const hasRedirectedRef = useRef(false);
  const mountedRef = useRef(false);
  
  // Log przy pierwszym renderze
  if (!mountedRef.current) {
    mountedRef.current = true;
    // console.log('[OnlineStatusProvider] MOUNTED - Initial state:', {
    //   isOnline: onlineStatus.isOnline,
    //   quality: onlineStatus.connectionQuality,
    //   navigatorOnline: navigator.onLine
    // });
  }
  
  // console.log('[OnlineStatusProvider] Status update:', {
  //   isOnline: onlineStatus.isOnline,
  //   quality: onlineStatus.connectionQuality,
  //   previousOnline: previousOnlineRef.current,
  //   hasRedirected: hasRedirectedRef.current,
  //   navigatorOnline: navigator.onLine
  // });
  
  useEffect(() => {
    // console.log('[OnlineStatusProvider] Redirect check:', {
    //   redirectToOfflineMode,
    //   previousOnline: previousOnlineRef.current,
    //   currentOnline: onlineStatus.isOnline,
    //   quality: onlineStatus.connectionQuality,
    //   pathname: window.location.pathname,
    //   hasRedirected: hasRedirectedRef.current
    // });

    // Przekieruj na stronę offline gdy:
    // 1. Jest włączone przekierowanie
    // 2. Status to offline
    // 3. Jeszcze nie przekierowaliśmy
    if (
      redirectToOfflineMode && 
      !onlineStatus.isOnline &&
      onlineStatus.connectionQuality === 'offline' &&
      !hasRedirectedRef.current
    ) {
      // Sprawdź czy nie jesteśmy już na stronie offline-mode
      if (!window.location.pathname.includes('offline-mode')) {
        console.log('[OnlineStatusProvider] Redirecting to offline mode');
        hasRedirectedRef.current = true;
        
        const navigatePromise = router.navigate({ to: '/offline-mode' });
        if (navigatePromise) {
          void navigatePromise.catch((error: Error) => {
            console.error('[OnlineStatusProvider] Failed to navigate to offline mode:', error);
            hasRedirectedRef.current = false; // Reset on error
          });
        }
      }
    }
    
    // Jeśli wrócimy online, zresetuj flagę przekierowania i przekieruj ze strony offline-mode
    if (onlineStatus.isOnline && onlineStatus.connectionQuality === 'good') {
      // console.log('[OnlineStatusProvider] Back online, resetting redirect flag');
      hasRedirectedRef.current = false;
      
      // Jeśli jesteśmy na stronie offline-mode, przekieruj do strony głównej
      if (window.location.pathname.includes('offline-mode')) {
        console.log('[OnlineStatusProvider] Redirecting from offline-mode to home');
        const navigatePromise = router.navigate({ to: '/' });
        if (navigatePromise) {
          void navigatePromise.catch((error: Error) => {
            console.error('[OnlineStatusProvider] Failed to navigate to home:', error);
          });
        }
      }
    }
    
    previousOnlineRef.current = onlineStatus.isOnline;
  }, [onlineStatus.isOnline, onlineStatus.connectionQuality, redirectToOfflineMode, router]);
  
  return (
    <OnlineStatusContext.Provider value={onlineStatus}>
      {children}
    </OnlineStatusContext.Provider>
  );
};
